<template>
  <div class="container">
    <div class="trials-filter-container" :class="{ 'phase-tab-disabled': treatmentsSelected }">
      <div id="phase-1-div" class="trial-phase" @click="phaseButtonClick(1)"
        :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="material-symbols-outlined size-20" :class="{ 'phase-tab-disabled': treatmentsSelected }">
          <span class="material-symbols-outlined" :class="{ 'phase-tab-disabled': treatmentsSelected }"
            :disabled="{ 'phase-tab-disabled': treatmentsSelected }">
            check_box{{ phaseIcon1 }}
          </span>
        </span>
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }"> Phase 1</span>
      </div>
      <div id="phase-2-div" class="trial-phase" @click="phaseButtonClick(2)"
        :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="material-symbols-outlined size-20">
          <span class="material-symbols-outlined" :class="{ 'phase-tab-disabled': treatmentsSelected }">
            check_box{{ phaseIcon2 }}
          </span>
        </span>
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }"> Phase 2</span>
      </div>
      <div id="phase-1-div" class="trial-phase" @click="phaseButtonClick(3)"
        :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="material-symbols-outlined size-20">
          <span class="material-symbols-outlined" :class="{ 'phase-tab-disabled': treatmentsSelected }">
            check_box{{ phaseIcon3 }}
          </span>
        </span>
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }"> Phase 3</span>
      </div>
      <div class="trial-phase distance-group" :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }">Within </span>
        <input v-model="distance" type="text" inputmode="numeric" class="distance-input"
          :class="{ 'phase-tab-disabled': treatmentsSelected }" :disabled="treatmentsSelected" min="1" max="24901">
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }">Miles</span>
      </div>
    </div>
    <div class="spinner-div" :class="{ 'spinner': spinnerOn }">
    </div>
    <div class="thera-label">
      {{ theraLabel }} <span class="count">{{ treatmentsSelected ? theraCount : filteredCount }}</span>
    </div>
    <div v-if="!showDetails" class="treatments-trials">
      <div v-if="treatmentsSelected" class="treatments-trials">
        <div v-for="treatment in activeTr" :key="treatment.id">
          <Treatment :name="treatment.name" :description="treatment.description"
            @open-details-modal="onDetailsModalOpen">
          </Treatment>
        </div>
      </div>
      <div v-else class="treatments-trials">
        <div v-for="trial in activeTr" :key="trial.id">
          <Trial :id="trial.id" :description="trial.briefTitle" :nearestLocation="trial.nearest_location"
            :phase="trial.phase" @open-details-modal="onDetailsModalOpen">
          </Trial>
        </div>
      </div>
    </div>
    <div v-else class="details-modal">
      <DetailsModal @close-details-modal="onDetailsModalClose" :title="showDetailsTitle" :text="showDetailsText"
        :type="showDetailsType">
      </DetailsModal>
    </div>
    <div v-if="enableAdvancedTrialSearch" class="advanced-search-button">
      <button class="primary-outlined-button report-button" @click="onAdvancedSearchOpen">
        <span class="material-symbols-outlined file-icon">lab_research</span>
        Advanced Search
      </button>
    </div>
    <div class="pagination" v-show="theraCount">
      <TreatmentsTrialsPagination :card-list="treatmentsSelected ? fullList : filteredTrialList"
        :set-page-one="setPageOne" @onCurrentPageChange="onCurrentPageUpdateActiveTr">
      </TreatmentsTrialsPagination>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineProps, onMounted, ref, toRaw, watch, watchEffect } from 'vue'
import Treatment from './Treatment.vue'
import Trial from './Trial.vue'
import TreatmentsTrialsPagination from './TreatmentsTrialsPagination.vue'
import DetailsModal from './DetailsModal.vue'
import { useStore } from 'vuex'
import { getEncryptedUrl, getSearchKey, getTreatments, getTrials } from './getGenomicsData.js'
import { advancedSearchUrl, defaultPostalCode, enableAdvancedTrialSearch } from '@/settings.js'

const MAX_CARDS = 6

const props = defineProps({
  alterations: Array,
  // flag indicating the genomics variants are loading
  loadingGenomics: Boolean,
  // flag indicatin the variants and the treatments & trials for the first variant are still loading
  stillFetchingVariants: Boolean,
  selectedTier: String,
  multiCardClicked: Number,
})

const advancedSearchIframeSrcUrl = ref('')
const showDetails = ref(false)
const showDetailsTitle = ref('')
const showDetailsText = ref('')
const showDetailsType = ref('')
const activeTr = ref({}) //represents the currently displayed slice of treatments/trials cards
const fullList = ref([]) //represents the full list of treatments or trials
const filteredTrialList = ref([]) //just for trial filtering based on phase and location
const theraCount = ref(0)
const filteredCount = computed(() => filteredTrialList.value?.length || 0) //just for trials
const phaseIcon1 = ref('')
const phaseIcon2 = ref('')
const phaseIcon3 = ref('')
const phase1Selected = ref(true)
const phase2Selected = ref(true)
const phase3Selected = ref(true)
const treatmentsSelected = ref(true)
const setPageOne = ref(false)
const distance = ref('')
const spinnerOn = ref(false)

const store = useStore()
const genomicsResults = computed(() => store.getters.session?.genomicsResults || {})
const postalCode = computed(() => store.getters.session?.patient?.postalCode ||
  store.getters.session?.patient?.postal_code || defaultPostalCode || '')
const diagnosis = computed(() => store.getters.session?.genomicsResults?.diagnosis || '')
const patient = computed(() => store.getters.session.patient)

const phaseIconLookup = { 1: phaseIcon1, 2: phaseIcon2, 3: phaseIcon3 }
const phaseSelectedLookup = { 1: phase1Selected, 2: phase2Selected, 3: phase3Selected }

const onAdvancedSearchOpen = async () => {
  const alterationValues = props.alterations.map((item) => item.value)
  //We include defaults here because these are required fields for the API
  const distanceValue = distance.value || '100'
  const diagnosisValue = diagnosis.value || 'none'
  const postalCodeValue = postalCode.value || defaultPostalCode
  const searchKeyResponse = await getSearchKey(diagnosisValue, distanceValue, postalCodeValue, alterationValues)

  const results = await getEncryptedUrl()
  if (!results) { return }
  const client = results.client || ''
  const iv = results.iv || ''
  const cp = results.cp || ''
  const queryParams = new URLSearchParams({
    client,
    iv,
    cp,
  })

  const searchKeyParam = searchKeyResponse?.searchKey ? `#sk/${searchKeyResponse.searchKey}` : ''
  advancedSearchIframeSrcUrl.value = `${advancedSearchUrl}/?${queryParams.toString()}${searchKeyParam}`

  store.dispatch('showViewer', true)
  store.dispatch('reportDocType', 'html')
  store.dispatch('reportDocument', advancedSearchIframeSrcUrl)
}

const onDetailsModalClose = () => {
  showDetails.value = false
}

const onDetailsModalOpen = (title, text, type) => {
  showDetailsTitle.value = title
  showDetailsText.value = text
  showDetails.value = true
  showDetailsType.value = type
}

const phaseButtonClick = (n) => {
  if (treatmentsSelected.value) return
  phaseIconLookup[n].value = phaseIconLookup[n].value === '' ? '_outline_blank' : ''
  phaseSelectedLookup[n].value = !phaseSelectedLookup[n].value
  filterTrialList()
  //because filtering should reduce page numbers, go to page 1
  onSetPageOne()
}

const onSetPageOne = () => {
  setPageOne.value = true
}

const theraLabel = computed(() => {
  return genomicsResults.value.treatmentOrTrialType === 'treatments' ? 'Therapies' : 'Trials'
})

const onCurrentPageUpdateActiveTr = (n) => {
  const sliceMin = MAX_CARDS * (n - 1)
  const sliceMax = sliceMin + MAX_CARDS
  if (genomicsResults.value.treatmentOrTrialType === 'treatments') {
    activeTr.value = fullList.value.slice(sliceMin, sliceMax)
  }
  else {
    activeTr.value = filteredTrialList.value.slice(sliceMin, sliceMax)
  }
  setPageOne.value = false
}

const filterTrialList = () => {
  const compareDistance = (a, b) => {
    // A guiding idea here is that we assume an unknown distance of a trial
    // is greater than a known distance
    if (!a.nearest_location && !b.nearest_location) { return 0 }
    if (!a.nearest_location && b.nearest_location) { return 1 }
    if (a.nearest_location && !b.nearest_location) { return -1 }
    const aDist = a.nearest_location.distance
    const bDist = b.nearest_location.distance
    if (!aDist && !bDist) { return 0 }
    if (!aDist && bDist) { return 1 }
    if (aDist && !bDist) { return -1 }
    return aDist - bDist
  }

  let filterArray = []
  filterArray = filterArray.concat(toRaw(fullList.value))

  if (!phase1Selected.value) {
    filterArray = filterArray.filter((trial) => trial.phase !== 'Phase 1'
      && trial.phase !== 'Early Phase 1' && trial.phase !== 'Early 1' && trial.phase !== '1')
  }
  if (!phase2Selected.value) {
    filterArray = filterArray.filter((trial) => trial.phase !== 'Phase 2' && trial.phase !== '2')
  }
  if (!phase3Selected.value) {
    filterArray = filterArray.filter((trial) => trial.phase !== 'Phase 3' && trial.phase !== '3')
  }
  // for those Phase1/Phase2 and Phase2/Phase3 cases
  if (!phase1Selected.value && !phase2Selected.value) {
    filterArray = filterArray.filter((trial) => !trial.phase?.includes('1') && !trial.phase?.includes('2'))
  }
  if (!phase2Selected.value && !phase3Selected.value) {
    filterArray = filterArray.filter((trial) => !trial.phase?.includes('2') && !trial.phase?.includes('3'))
  }
  if (distance.value) {
    filterArray = filterArray.filter((trial) => Math.round(trial.nearest_location?.distance) <= distance.value)
  }

  // filterArray = filterArray.toSorted(compareDistance) //doesn't work with our jest/node.js version; browser only
  filterArray = [...filterArray].sort(compareDistance) //does work with jest/node.js as well as browser

  filteredTrialList.value = filterArray
  activeTr.value = filteredTrialList.value.slice(0, MAX_CARDS)
}

const populateFullList = async (treatmentOrTrialType, alter) => {
  if (!fullList.value.length || theraCount.value === 0) { return }

  const controller = new AbortController()
  const MAX_DISTANCE = '24000'
  const MAX_RETRIES = 15
  const MAX_REQUESTS = 10
  const location = postalCode.value
  let accumArray = []
  // const timerStart = performance.now()

  accumArray = accumArray.concat(toRaw(fullList.value))


  // The intention of this function is to handle the case where the user has clicked to Treatments or Trials of a different alteration or from
  // Trials to Treatments of the same alteration or vice versa, before the previously selected is done loading, and the previously selected
  // had more promises still inbound. Often this happens going from many hundreds of trials for one alteration but only a few for the next.
  const cancelFetchingCheck = () => {
    if (alter.originalAlt !== genomicsResults.value.selectedAlteration ||
      ((treatmentOrTrialType === 'treatments') !== treatmentsSelected.value)) {
      controller.abort()
      return 1
    }
    return 0
  }
  if (cancelFetchingCheck()) { return }

  spinnerOn.value = true
  const incrementer = MAX_CARDS * 3 * MAX_REQUESTS

  //we want to chunk the promises up such that we only do MAX_REQUESTS promises/requests at a time
  for (let chunkStart = MAX_CARDS * 3; chunkStart < theraCount.value; chunkStart += incrementer) {
    const promises = []
    const starts = []

    for (let start = chunkStart; start < Math.min(theraCount.value, chunkStart + incrementer); start += MAX_CARDS * 3) {
      if (treatmentOrTrialType === 'treatments') {
        const treatmentArgs = {
          controller,
          diagnosis: diagnosis.value,
          omicsType: alter.type,
          omicsValue: alter.value,
          start,
        }
        promises.push(treatmentArgs)
      } else {
        const trialArgs = {
          age: patient.value.age,
          controller,
          diagnosis: diagnosis.value,
          distance: MAX_DISTANCE,
          gender: patient.value.gender,
          location,
          omicsType: alter.type,
          omicsValue: alter.value,
          sampleId: alter.mostRecentAccessionNum,
          start,
        }
        promises.push(getTrials(trialArgs))
      }
      starts.push(start)
    }

    let retryCounter = 0
    const treatmentsTrialsPromiseAll = async (array, starts) => {
      const retryPromises = []
      const retryStarts = []
      await Promise.allSettled(array).then(results => {
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const response = result.value
            if (!response.rows) {
              if (treatmentOrTrialType === 'treatments') {
                const treatmentArgs = {
                  controller,
                  diagnosis: diagnosis.value,
                  omicsType: alter.type,
                  omicsValue: alter.value,
                  start: starts[index],
                }
                retryPromises.push(getTreatments(treatmentArgs))
                retryStarts.push(starts[index])
                return
              }
              const trialArgs = {
                age: patient.value.age,
                controller,
                diagnosis: diagnosis.value,
                distance,
                gender: patient.value.gender,
                location,
                omicsType: alter.type,
                omicsValue: alter.value,
                sampleId: alter.mostRecentAccessionNum,
                start: starts[index],
              }
              retryPromises.push(getTrials(trialArgs))
              retryStarts.push(starts[index])
              return
            }
            accumArray = accumArray.concat(response.rows.slice(0, MAX_CARDS * 3))
          } else if (result.rows) {
            accumArray = accumArray.concat(result.rows.slice(0, MAX_CARDS * 3))
          } else {
            console.error('Failed treatment or trial fetch: ', result.status, result.reason)
          }
        })
      })
      // Don't even make the recursive call if we're no longer the current treatment/trial
      if (cancelFetchingCheck()) { return }

      if (retryPromises.length && retryCounter < MAX_RETRIES) {
        retryCounter += 1
        await treatmentsTrialsPromiseAll(retryPromises, retryStarts)
      }
    }
    await treatmentsTrialsPromiseAll(promises, starts)
  }

  if (cancelFetchingCheck()) { return }
  fullList.value = accumArray
  filterTrialList()
  spinnerOn.value = false
  // const timerEnd = performance.now()
  // console.log(`execution time: ${timerEnd - timerStart} ms`)
}

// We're changing the selectedAlteration, or treatment or trial type.
// responding to user selection from the Variants icons
watch(() => (genomicsResults.value.selectedAlteration, genomicsResults.value.treatmentOrTrialType), async () => {
  onDetailsModalClose()
  theraCount.value = genomicsResults.value.treatmentOrTrialCount
  treatmentsSelected.value = genomicsResults.value.treatmentOrTrialType === 'treatments'

  // shouldn't run if we're still loading genomics variants
  if (!props.alterations.length || props.stillFetchingVariants || props.loadingGenomics) { return }

  const newlySelectedAlt = props.alterations.find(alter => alter.originalAlt === genomicsResults.value.selectedAlteration)

  if (genomicsResults.value.treatmentOrTrialType === 'treatments') {
    if (!newlySelectedAlt || !newlySelectedAlt.treatmentRows) { return }
    activeTr.value = newlySelectedAlt.treatmentRows.slice(0, MAX_CARDS)
    // store off the first 3 pages, since we have them (20 from annotation)
    // TODO: may be able to make this cleaner depending on backend API support
    fullList.value = newlySelectedAlt.treatmentRows.slice(0, MAX_CARDS * 3)
    await populateFullList('treatments', newlySelectedAlt)
  }
  else {
    if (!newlySelectedAlt || !newlySelectedAlt.trialRows) { return }
    activeTr.value = newlySelectedAlt.trialRows.slice(0, MAX_CARDS)
    // store off the first 3 pages, since we have them
    // TODO: may be able to make this cleaner depending on backend API support
    fullList.value = newlySelectedAlt.trialRows.slice(0, MAX_CARDS * 3)
    // const timerStart = performance.now()

    await populateFullList('trials', newlySelectedAlt)
    // const timerEnd = performance.now()
    // console.log(`execution time to obtain the full list of trials: ${timerEnd - timerStart} ms`)
  }
},
// This watch won't occur without this deep watcher syntax
// https://vuejs.org/guide/essentials/watchers.html#deep-watchers
{ deep: true },
)

// currently, display the first page of treatments for the top alteration
// after all of the alteration fetching is done and we have the first set of
// treatments and trials
watch(() => props.stillFetchingVariants, async (stillFetchingVariants) => {
  if (!props.alterations.length || stillFetchingVariants) { return }
  const firstAlter = props.alterations[0]
  activeTr.value = firstAlter.treatmentRows.slice(0, 6)
  const cardState = {
    selectedAlteration: props.alterations[0].originalAlt || '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: props.alterations[0].treatmentsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
  await populateFullList('treatments', firstAlter)
})

//Tier (clinical significance) switching
watch(() => props.selectedTier, async (selectedTier) => {
  if (!props.alterations.length || !selectedTier) {
    activeTr.value = []
    fullList.value = []
    filteredTrialList.value = []
    theraCount.value = 0
    return
  }
  const firstAlter = props.alterations[0]
  activeTr.value = firstAlter.treatmentRows.slice(0, 6)
  const cardState = {
    selectedAlteration: props.alterations[0].originalAlt || '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: props.alterations[0].treatmentsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
  await populateFullList('treatments', firstAlter)
})

// we've selected a new sample, so the intention here is to
// clear stuff out
watch(() => props.loadingGenomics, (loadingGenomics) => {
  if (!loadingGenomics) { return }
  activeTr.value = []
  fullList.value = []
  filteredTrialList.value = []
  theraCount.value = 0
  const cardState = {
    selectedAlteration: '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: 0,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
})

// watch multiCardClicked since the focused treatment may need to change
watch(() => props.multiCardClicked, () => {
  if (!props.alterations.length) { return }
  const firstAlter = props.alterations[0]
  activeTr.value = firstAlter.treatmentRows.slice(0, MAX_CARDS)
  const cardState = {
    selectedAlteration: props.alterations[0].originalAlt || '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: props.alterations[0].treatmentsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
})

watchEffect(() => {
  // not filtering treatements based on distance, just trials
  if (treatmentsSelected.value) { return }
  filterTrialList()
  onSetPageOne()
})

//lets get our selection back if we refresh
onMounted(() => {
  if (!props.alterations.length) { return }
  activeTr.value = props.alterations[0].treatmentRows.slice(0, 6)
  const cardState = {
    selectedAlteration: props.alterations[0].originalAlt || '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: props.alterations[0].treatmentsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
})
</script>

<style lang="scss" scoped>
.advanced-search-button {
  padding-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .primary-outlined-button {
    display: flex;
    align-items: center;
    margin-left: 10px;

    span {
      padding-right: 8px;
      font-size: 20px;
    }
  }
}

.container {
  margin-left: 0.5rem;
}

.treatments-trials {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: calc(63vh - 14rem);
  width: fit-content;
  overflow: auto;
  justify-content: flex-start;
  align-content: flex-start;
  gap: 15px 0;
}

.details-modal {
  margin-left: 10px;
  flex-direction: row;
  flex-wrap: wrap;
  width: auto;
  height: calc(63vh - 14rem);
  width: calc(23vw - 2rem);
  background-color: #F7F9FB;
  overflow: auto;
  border-radius: 9.72px;
  box-shadow: 0 0 6px #b9b9b9;
}

.pagination {
  margin-top: 10px;
  display: flex;
  color: var(--Grey600);
  width: calc(20vw - 2rem);
}


.phase-text {
  width: calc(4vw - 2rem);
  margin-right: 0.25rem;
}

.phase-tab-disabled {
  user-select: none;
  color: var(--Grey200);
  cursor: default;

  &:hover {
    cursor: default;
  }

  &>input {
    background-color: var(--Grey200);
  }
}

.thera-label {
  margin: 0.5rem 0 3px 10px;
  color: var(--Grey500);
  width: fit-content;
  height: 16.2px;
  flex-shrink: 0;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.04px;

  &>.count {
    font-weight: 700;
    min-width: 15px;
    padding: 4px;
    color: #fff;
    background-color: var(--Secondary);
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
    font-weight: 800;
    border-radius: 10px;
    margin-left: 0;
    margin-top: -12px;
  }
}

.trials-filter-container {
  display: flex;
  justify-content: space-evenly;
  color: var(--Grey500);
  font-size: 60%;
  align-items: left;
  justify-content: left;
  text-align: left;
  user-select: none;
  width: calc(2vw - 2rem);
  gap: max(0.4rem, calc(1vw - 1rem));
}

.trial-phase {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: var(--Grey100);
  padding: 4px;
  border-radius: 5px;
  text-align: center;
  color: var(--Grey600);

  cursor: pointer;

  &:hover {
    color: var(--Black);
  }
}

.distance-group {
  width: max(9rem, calc(8vw - 1rem));
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
}

.distance-input {
  border: none;
  margin: 5px 0 5px 5px;
  width: calc(6vw - 2.5rem);
  max-width: 4rem;

  height: 20px;
  border-radius: 6px;
  background: #E5ECF6;
}

*:disabled {
  background-color: dimgrey;
  color: linen;
  opacity: 1;
}
</style>
